import React, { forwardRef } from 'react'
import { Box, IconButton, Theme, Tooltip, useMediaQuery } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { CustomLayout } from 'src/Libs/chart-kit/shared/utils/chartModel'
import { J2Editacontent, J2Delete, J2Document, J1Diagonalarrowzoom } from '@e3dc-react/icons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import useModals, { SystemChartProps } from 'src/Hooks/useModals'
import { useParams } from 'react-router-dom'
import { printChartData, HIDE_ON_PRINT_CLASS } from 'src/Libs/chart-kit/shared/utils/print-chart-data'
import { ChartSettings } from '../../../chart-kit/editor'
import { useLastTimeFetched } from '../../../chart-kit/shared/hooks'
import { Typography } from '@e3dc-react/shell/Elements/Typography'
import PieSystemChartHeader from './PieSystemChartHeader'
import { useNewRelic } from '@e3dc-react/shell/Libs/observability-tool'
import { useUserSettings } from 'src/Hooks'
import VisibilityIcon from 'src/Elements/VisibilityIcon'
import ExportHistoryData from './ExportHistoryData'
import { DEFAULT_CHART_ID, DEFAULT_POC_CHART_ID } from '../../default-charts/utils/initial-chart-config'

const useStyles = makeStyles()(theme => ({
  headerChildren: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
  },
  headerButtons: {
    display: 'flex',
    position: 'relative',
    top: 0,
    right: 0,
  },
  title: {
    width: '100%',
    textAlign: 'left',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    justifyContent: 'flex-start',
    [theme.breakpoints.down('lg')]: {
      textAlign: 'start',
    },
  },
  iconButton: {
    color: theme.palette.primary.main,
  },
}))

interface Props {
  isPieChart: boolean
  title: string
  customLayout?: CustomLayout
  systemChartProps: SystemChartProps
  toggleLegendVisibility: () => void
  setDownloadLoading: (downloading: boolean) => void
  handleDeleteChart?: (id: number | string) => void
  handleSaveChart?: (props: ChartSettings) => void
  tooltipText?: string
  resetZoom: () => void
  isZoomedIn: boolean
  pieChartHeaderAction?: React.ReactElement
  hideLegend?: boolean
}

const SystemChartHeader = forwardRef<HTMLDivElement, Props>(
  (
    {
      isPieChart,
      title,
      customLayout,
      systemChartProps,
      toggleLegendVisibility,
      hideLegend,
      setDownloadLoading,
      handleDeleteChart,
      handleSaveChart,
      resetZoom,
      isZoomedIn,
      tooltipText,
      pieChartHeaderAction,
    },
    ref
  ) => {
    const { classes } = useStyles()
    const { t } = useCustomTranslation()
    const { openSystemChartEditor, openConfirmationModal } = useModals()
    const { pageActions } = useNewRelic()
    const { userSettings, isUserSettingsLoading } = useUserSettings()
    const downSm = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))
    const upMd = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'))
    const { storageId, installationId } = useParams<{ storageId: string; installationId: string }>()
    const historyDataFetchDate = useLastTimeFetched()

    const handlePrintChartData = (): void => {
      pageActions.componentClicked({
        componentName: 'SystemChartHeader',
        elementName: 'printChartData',
        userId: userSettings.userid?.toString(),
      })
      const isDefaultPerformanceValuesChart = customLayout?.id === DEFAULT_CHART_ID || customLayout?.id === DEFAULT_POC_CHART_ID

      printChartData(
        `chart-${customLayout?.title}-${customLayout?.id}`,
        title,
        installationId,
        storageId,
        historyDataFetchDate,
        setDownloadLoading,
        t,
        isDefaultPerformanceValuesChart
      )
    }

    if (isPieChart) return <PieSystemChartHeader title={title} tooltipText={tooltipText} ref={ref} action={pieChartHeaderAction} />

    return (
      <Box ref={ref} className={classes.headerChildren}>
        <Typography variant="h3" className={classes.title}>
          {title}
        </Typography>
        <Box className={`${classes.headerButtons} ${HIDE_ON_PRINT_CLASS}`} sx={{ flex: 1, justifyContent: 'end' }}>
          <Tooltip title={t('systemDataHandler.chartHeader.reset-chart-zoom')}>
            <Box component="span">
              <IconButton
                disabled={!isZoomedIn}
                onClick={resetZoom}
                data-qa={`button-reset-zoom-${customLayout?.id}`}
                size="large"
                className={classes.iconButton}
              >
                <J1Diagonalarrowzoom />
              </IconButton>
            </Box>
          </Tooltip>
          {upMd && (
            <Tooltip title={t('systemDataHandler.chartHeader.print')}>
              <Box component="span">
                <IconButton
                  onClick={handlePrintChartData}
                  data-qa={`button-print-chart-${customLayout?.id}`}
                  size="large"
                  className={classes.iconButton}
                  disabled={isUserSettingsLoading}
                >
                  <J2Document />
                </IconButton>
              </Box>
            </Tooltip>
          )}
          {upMd && (
            <ExportHistoryData
              customLayout={customLayout}
              filename={`${title}`}
              tooltip={t('systemDataHandler.chartHeader.export')}
              dataQa={`button-export-csv-chart-${customLayout?.id}`}
            />
          )}
          <Tooltip title={t('systemDataHandler.editor.editChart')}>
            <IconButton
              onClick={() => openSystemChartEditor({ ...systemChartProps, handleSaveChart, serialNumber: storageId })}
              className={classes.iconButton}
              data-qa={`button-edit-chart-${customLayout?.id}`}
              size="large"
            >
              <J2Editacontent />
            </IconButton>
          </Tooltip>
          {!customLayout?.isInitial && handleDeleteChart && (
            <Tooltip title={t('systemDataHandler.chartHeader.remove')}>
              <Box component="span">
                <IconButton
                  onClick={() =>
                    customLayout?.id
                      ? openConfirmationModal({
                          onConfirm: onClose => {
                            handleDeleteChart?.(customLayout!.id!)
                            onClose()
                          },
                          title: t('systemDataHandler.editor.removeChartTitle'),
                          text: t('systemDataHandler.editor.removeChartText'),
                          buttonText: [t('common.cancel'), t('systemDataHandler.chartHeader.remove')],
                        })
                      : undefined
                  }
                  data-qa={`button-delete-chart-${customLayout?.id}`}
                  size="large"
                  className={classes.iconButton}
                >
                  <J2Delete />
                </IconButton>
              </Box>
            </Tooltip>
          )}
          {downSm && <VisibilityIcon isHidden={Boolean(hideLegend)} onClick={() => toggleLegendVisibility()} />}
        </Box>
      </Box>
    )
  }
)

SystemChartHeader.displayName = 'SystemChartHeader'

export default SystemChartHeader
